import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card.js';
import { DollarSign, Loader2, CreditCard, Trash2 } from 'lucide-react';
import { Button } from '../ui/button.js';
import { settingsService } from '../../services/settingsService.js';
import type { BillingSubscription, PaymentMethod } from '../../services/settingsService';
import { toast } from 'react-hot-toast';

const BillingTab: React.FC = () => {
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const loadBillingData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [subscriptionData, paymentMethodsData] = await Promise.all([
          settingsService.loadSubscription(),
          settingsService.loadPaymentMethods()
        ]);
        setSubscription(subscriptionData);
        setPaymentMethods(Array.isArray(paymentMethodsData) ? paymentMethodsData : []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load billing data';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    loadBillingData();
  }, []);

  const handlePlanChange = async (planId: string) => {
    if (!subscription) return;
    
    setUpdating(true);
    try {
      const updatedSubscription = await settingsService.updateSubscription({ planId });
      setSubscription(updatedSubscription);
      toast.success('Plan updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update plan';
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    setUpdating(true);
    try {
      const updatedSubscription = await settingsService.updateSubscription({ 
        cancelAtPeriodEnd: !subscription.cancelAtPeriodEnd 
      });
      setSubscription(updatedSubscription);
      toast.success(updatedSubscription.cancelAtPeriodEnd ? 'Subscription will be canceled at period end' : 'Subscription cancellation removed');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update subscription';
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    try {
      await settingsService.deletePaymentMethod(paymentMethodId);
      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
      toast.success('Payment method removed');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove payment method';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Billing</h2>
          <p className="text-slate-400 mt-1">Manage your subscription and payment methods</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-brand-accent" />
          <span className="ml-3 text-slate-400">Loading billing information...</span>
        </div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Billing</h2>
          <p className="text-slate-400 mt-1">Manage your subscription and payment methods</p>
        </div>
        <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error || 'Failed to load billing information'}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => window.location.reload()}
            className="mt-3"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Billing</h2>
        <p className="text-slate-400 mt-1">
          Manage your subscription and payment methods
        </p>
      </div>

      {/* Current Subscription */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <DollarSign className="w-5 h-5 text-green-400" />
          <h4 className="text-base font-medium text-white">Current Subscription</h4>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            subscription.status === 'active' ? 'bg-green-500/20 text-green-400' :
            subscription.status === 'past_due' ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {subscription.status?.toUpperCase() || 'UNKNOWN'}
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
            <div>
              <p className="text-sm font-medium text-slate-300">{subscription.plan?.name || 'Unknown Plan'}</p>
              <p className="text-xs text-slate-500">{subscription.plan?.features?.join(', ') || 'No features listed'}</p>
              {subscription.cancelAtPeriodEnd && (
                <p className="text-xs text-yellow-400 mt-1">Will be canceled on {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-green-400">${subscription.plan?.price || '0'}/{subscription.plan?.interval || 'month'}</p>
              <p className="text-xs text-slate-500">Next billing: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-slate-700 rounded-lg text-center">
              <p className="text-lg font-semibold text-white">{subscription.usage?.apiCalls?.toLocaleString() || '0'}</p>
              <p className="text-xs text-slate-500">API calls used</p>
              <p className="text-xs text-slate-400">of {subscription.limits?.apiCalls === -1 ? '∞' : subscription.limits?.apiCalls?.toLocaleString() || '0'}</p>
            </div>
            <div className="p-3 bg-slate-700 rounded-lg text-center">
              <p className="text-lg font-semibold text-white">{subscription.usage?.storage || '0'}GB</p>
              <p className="text-xs text-slate-500">Storage used</p>
              <p className="text-xs text-slate-400">of {subscription.limits?.storage || '0'}GB</p>
            </div>
            <div className="p-3 bg-slate-700 rounded-lg text-center">
              <p className="text-lg font-semibold text-white">{subscription.usage?.bandwidth || '0'}GB</p>
              <p className="text-xs text-slate-500">Bandwidth used</p>
              <p className="text-xs text-slate-400">of {subscription.limits?.bandwidth || '0'}GB</p>
            </div>
          </div>

          <div className="flex gap-3">
            <select
              onChange={(e) => handlePlanChange(e.target.value)}
              disabled={updating}
              className="flex-1 p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              value={subscription.plan?.id || 'starter'}
            >
              <option value="starter">Starter Plan - $9.99/month</option>
              <option value="pro">Pro Plan - $29.99/month</option>
              <option value="enterprise">Enterprise Plan - $99.99/month</option>
            </select>
            <Button
              variant="ghost"
              onClick={handleCancelSubscription}
              disabled={updating}
              className="text-red-400 hover:text-red-300"
            >
              {updating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : subscription.cancelAtPeriodEnd ? (
                'Reactivate'
              ) : (
                'Cancel'
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Payment Methods */}
      <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-blue-400" />
          <h4 className="text-base font-medium text-white">Payment Methods</h4>
        </div>
        
        <div className="space-y-4">
          {paymentMethods.length === 0 ? (
            <p className="text-slate-400 text-sm">No payment methods added</p>
          ) : (
            paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-300">
                      **** **** **** {method.card?.last4 || 'XXXX'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {method.card?.brand?.toUpperCase() || 'UNKNOWN'} • Expires {method.card?.expMonth || 'XX'}/{method.card?.expYear || 'XXXX'}
                    </p>
                  </div>
                  {method.isDefault && (
                    <div className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                      Default
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeletePaymentMethod(method.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            ))
          )}
          
          <Button variant="outline" className="violett-button text-drop-shadow w-full">
            Add Payment Method
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default BillingTab;
